import { has } from 'lodash'
import PropTypes from 'prop-types'
import React, { Component } from 'react'

import {
  createDateFromYYYYMMDD,
  createYYYYMMDDFromDate,
} from '../../../utilities/DateUtils'
import * as FormConstants from '../../../utilities/FormHelperUtils'
import {
  ERROR_CLASS,
  getMandatoryIndicator,
  shouldRenderAsHTML,
} from '../../../utilities/FormHelperUtils'
import {
  cloneDeepJSON,
  isEmpty,
  isNumberEven,
  merge,
} from '../../../utilities/GeneralUtils'
import SimpleAnimate, { CMP_TYPE_DIV } from '../../animation/SimpleAnimate'
import AButtonStandard from '../../button/AButtonStandard'
import AExpandPanelCustom from '../../container/AExpandPanelCustom'
import BoxContainer from '../../container/BoxContainer'
import HighlightContainer, {
  HIGHLIGHT_NOTIFICATION,
} from '../../container/HighlightContainer'
import SimpleExpandPanel from '../../container/SimpleExpandPanel'
import ErrorInline, { getInlineError } from '../../error/ErrorInline'
import ACol from '../../layout/ACol'
import ARow from '../../layout/ARow'
import Cont, { ALIGN_RIGHT } from '../../layout/Cont'
import Section, { SUB } from '../../layout/Section'
import ABaseLink from '../../link/ABaseLink'
import AImg from '../../media/img/AImg'
import TextBlock from '../../text/TextBlock'
import AInput from '../AInput'
import AInputCurrency from '../AInputCurrency'
import AInputDate from '../AInputDate'
import AInputHolder, * as AInputHolderConstants from '../AInputHolder'
import AInputTime from '../AInputTime'
import ARatingSelector from '../ARatingSelector'
import ButtonSelector from '../ButtonSelector'
import {
  convertObjToDynamicDataResponse,
  getInputType,
  getMaxDate,
  getMaxLength,
  getMaxNumber,
  getMinDate,
  getMinNumber,
  getNumberTypeStep,
  intlIt,
  isMandatory,
  validateInput,
} from './DynamicDataUtils'
import { actOnData, addMultichildCtrsToObject } from './MultichildUtils'

export const LAYOUT_BOTTOM = 'layoutBottom'
export const LAYOUT_LEFT = 'layoutLeft'
export const LAYOUT_RIGHT = 'layoutRight'
export const LAYOUT_TOP = 'layoutTop'
export const TYPE_LABEL = 'typeLabel'
export const TYPE_IMAGE = 'typeImage'
export const TYPE_INFOBLOCK = 'typeInfoblock'
export const TYPE_OPTIONS = 'typeOptions'
export const TYPE_SELECTOR = 'typeSelector'
export const TYPE_TEXT = 'typeText'
export const TYPE_MULTICHILD = 'typeMultichild'
export const TYPE_FORM = 'typeForm'
export const TYPE_DATE = 'typeDate'
export const TYPE_TIME = 'typeTime'
export const TYPE_CURRENCY = 'typeCurrency'
export const OPTION_TYPE_SELECTABLE = 'SELECTABLE'
export const OPTION_TYPE_UNSELECTABLE = 'UNSELECTABLE'

/**
 * DynamicDataCmp
 * @example ../../../../../docs/external/form/dynamicData/DynamicDataCmp.md
 */
class DynamicDataCmp extends Component {
  // **************Class Methods**************
  // *****************************************
  constructor(props, context) {
    super(props, context)
    this.lastChangedInput = null
    this.state = {
      fieldValues: {},
      fieldErrors: {},
      validationRequestId: this.props.validationRequestId,
      data: {},
    }
    this.multichildCtrs = {}
    this.collapseStates = {}
  }

  componentDidUpdate(prevProps) {
    const { validationRequestId, data } = this.props

    // Check if data has changed and update component state accordingly
    if (data !== prevProps.data) {
      this.collapseStates = {}
      const localData = cloneDeepJSON(data)
      this.setState({
        data: localData,
        fieldValues: this.composefieldValues(data),
        validationRequestId: 0, // Reset validation to force fresh state
      })

      return
    }

    if (validationRequestId === prevProps.validationRequestId) return

    if (validationRequestId === 0) {
      this.collapseStates = {}
      const localData = cloneDeepJSON(data)
      this.setState({
        data: localData,
        fieldValues: this.composefieldValues(data),
        validationRequestId,
      })
    } else if (validationRequestId > this.state.validationRequestId) {
      this.validateAllInputs(validationRequestId)
    }
  }

  componentDidMount() {
    if (this.props.validationRequestId === 0) {
      this.collapseStates = {}
      const localData = cloneDeepJSON(this.props.data)
      this.setState({
        data: localData,
        fieldValues: this.composefieldValues(this.props.data),
      })
    } else {
      this.validateAllInputs(this.props.validationRequestId)
    }
  }

  resetData = () => {
    const { data } = this.props
    const localData = cloneDeepJSON(data)
    this.setState({
      data: localData,
      fieldValues: this.composefieldValues(data),
    })
  }

  render() {
    return <div className="">{this.getForm()}</div>
  }

  // **************Render Functions**************
  // *****************************************
  onInputChange(input, value) {
    this.lastChangedInput = input
    const { intlObj } = this.props
    if (input.type === TYPE_SELECTOR) {
      const currValue = this.getInputCurrentValue(input, this)
      if (currValue === 'true') {
        value = 'false'
      } else {
        value = 'true'
      }
    }
    const newFieldValues = {
      ...this.state.fieldValues,
      [input.fieldName]: value,
    }
    // When useStrictCurrentValues and user selects a non-empty option (e.g. equipment type
    // other than "-- Select Equipment --"), seed default values from that option's children
    // so fields like "all dates selected" render correctly without needing Add More.
    if (
      this.props.useStrictCurrentValues &&
      input.type === TYPE_OPTIONS &&
      input.options &&
      value !== null &&
      String(value) !== '-1'
    ) {
      const selectedOption = input.options.find(
        (opt) => opt && String(opt.value) === String(value),
      )
      if (selectedOption && selectedOption.children && selectedOption.children.length) {
        const childDefaults = this.getDefaultValuesFromInputs(selectedOption.children)
        Object.assign(newFieldValues, childDefaults)
      }
    }
    if (input.type === TYPE_TEXT) {
      this.setState({
        ...this.state,
        fieldValues: newFieldValues,
      })
    } else {
      let error = null
      const errors = validateInput(input, value, intlObj)

      if (!isEmpty(errors) && errors.length > 0) {
        error = errors[0]
      }

      this.setState({
        ...this.state,
        fieldValues: newFieldValues,
        fieldErrors: {
          ...this.state.fieldErrors,
          [input.fieldName]: error,
        },
      })
    }
  }

  onInputBlur(input, value) {
    if (input.type === TYPE_TEXT) {
      // console.log('onInputBlur ' + input.fieldName + '/' + value)
      const { intlObj } = this.props
      const errors = validateInput(input, value, intlObj)
      // this.addFieldErrors(input, errors, this)
      let error = null
      if (!isEmpty(errors) && errors.length > 0) {
        error = errors[0]
      }
      this.setState({
        ...this.state,
        fieldErrors: {
          ...this.state.fieldErrors,
          [input.fieldName]: error,
        },
      })
    }
  }

  renderTextInput(input, context) {
    const { intlObj } = context.props
    const label =
      intlIt(
        intlObj,
        input.labelToken,
        input.defaultLabel,
        {},
        input.labelParameters,
      ) + context.getMandatoryIndicator(input, context)
    let placeholder = ''
    if (!isEmpty(input.placeholder)) {
      placeholder = input.placeholder
    }

    return (
      <AInputHolder wrapperStyle={input.wrapperStyle}>
        <AInput
          id={input.fieldName}
          cmpRef={input.fieldName}
          label={label}
          onChange={(e) => {
            this.onInputChange(input, e.target.value)
          }}
          onBlur={(e) => {
            this.onInputBlur(input, e.target.value)
          }}
          type={getInputType(input)}
          rows={input.multiLineRows}
          value={context.getInputCurrentValue(input, context)}
          style={context.getInputStyle(input, context)}
          help={context.getInputError(input, context)}
          placeholder={placeholder}
          maxLength={getMaxLength(input)}
          step={getNumberTypeStep(input)}
          minNumber={getMinNumber(input)}
          maxNumber={getMaxNumber(input)}
          readOnly={input.readonly}
        />
        {context.renderNotes(input, context)}
      </AInputHolder>
    )
  }

  renderOptionsInput(input, context) {
    const { intlObj } = context.props
    let label
    if (!isEmpty(input.labelToken) || !isEmpty(input.defaultLabel)) {
      label = intlIt(
        intlObj,
        input.labelToken,
        input.defaultLabel,
        {},
        input.labelParameters,
      )
    }
    const displayLabel =
      typeof label === 'undefined'
        ? undefined
        : label + context.getMandatoryIndicator(input, context)
    const optionLabelValues = []
    let options = input.options
    if (!isEmpty(input.optionsLite)) {
      options = input.optionsLite
    }
    for (const option of options) {
      let defaultLabel = option.value
      if (!isEmpty(option.defaultLabel)) {
        defaultLabel = option.defaultLabel
      }
      const isHTML = option.defaultLabelHTML
      const optionLabel = intlIt(
        intlObj,
        option.labelToken,
        defaultLabel,
        {},
        option.labelParameters,
      )
      optionLabelValues.push({
        value: option.value,
        label: optionLabel,
        isHTML,
        notesUI: context.renderNotes(option, context),
        iconValue: option.iconValue,
        type: option.optionType,
      })
    }
    if (input.selectorType === 'rating') {
      const values = []
      const labels = []
      const htmlIndicators = []
      const optionIcons = []
      for (const option of optionLabelValues) {
        values.push(option.value)
        labels.push(option.label)
        htmlIndicators.push(option.isHTML)
      }

      return (
        <AInputHolder wrapperStyle={input.wrapperStyle}>
          <ARatingSelector
            id={input.fieldName}
            cmpRef={input.fieldName}
            label={label}
            optionLabels={labels}
            optionValues={values}
            optionIcons={optionIcons}
            htmlIndicators={htmlIndicators}
            selectedValues={[context.getInputCurrentValue(input, context)]}
            onSelect={(id, value) => {
              this.onInputChange(input, value)
            }}
            isMultiSelect={false}
            allowDeselection={false}
            formGroup
            style={context.getInputStyle(input, context)}
            help={[context.getInputError(input, context)]}
          />
          {context.renderNotes(input, context)}
        </AInputHolder>
      )
    } else if (!input.renderAllVisibe) {
      const optionsList = optionLabelValues.map((option, i) => (
        <option
          key={i}
          value={option.value}
          disabled={option.type === OPTION_TYPE_UNSELECTABLE}
        >
          {option.label}
        </option>
      ))

      return (
        <AInputHolder wrapperStyle={input.wrapperStyle}>
          <AInput
            id={input.fieldName}
            cmpRef={input.fieldName}
            label={displayLabel}
            onChange={(e) => {
              this.onInputChange(input, e.target.value)
            }}
            type={FormConstants.SELECT}
            value={context.getInputCurrentValue(input, context)}
            style={context.getInputStyle(input, context)}
            help={context.getInputError(input, context)}
            disabled={this.isSelectDisabled(
              optionsList,
              context.getInputCurrentValue(input, context),
            )}
          >
            {optionsList}
          </AInput>
          {context.renderNotes(input, context)}
        </AInputHolder>
      )
    }
    const values = []
    const labels = []
    const notesUIs = []
    const types = []
    const htmlIndicators = []
    const optionIcons = []
    for (const option of optionLabelValues) {
      values.push(option.value)
      labels.push(option.label)
      notesUIs.push(option.notesUI)
      htmlIndicators.push(option.isHTML)
      optionIcons.push(option.iconValue)
      types.push(option.type)
    }
    let selectedValues = context.getInputCurrentValue(input, context)
    if (input.multiSelect) {
      if (isEmpty(selectedValues)) {
        selectedValues = []
      }
    } else if (isEmpty(selectedValues)) {
      selectedValues = []
    } else {
      selectedValues = [selectedValues]
    }

    return (
      <AInputHolder wrapperStyle={input.wrapperStyle}>
        <ButtonSelector
          id={input.fieldName}
          cmpRef={input.fieldName}
          label={displayLabel}
          labelIsHTML={input.defaultLabelHTML}
          optionLabels={labels}
          optionsValues={values}
          optionIcons={optionIcons}
          optionNotesUIs={notesUIs}
          optionTypes={types}
          htmlIndicators={htmlIndicators}
          selectedValues={selectedValues}
          onSelect={(id, value) => {
            this.onInputChange(input, input.multiSelect ? value : value[0])
          }}
          // work around for allowDeselection - it should not be connected to multiSelect
          isMultiSelect={input.multiSelect}
          allowDeselection={input.multiSelect}
          formGroup
          style={context.getInputStyle(input, context)}
          help={[context.getInputError(input, context)]}
          selectorType={input.selectorType}
          isInline={input.inlineOptions}
          allowsBulkSelection={input.allowsBulkSelection}
          allowsBulkDeselection={input.allowsBulkDeselection}
        />
        {context.renderNotes(input, context)}
      </AInputHolder>
    )
  }

  renderSelectorInput(input, context) {
    const { intlObj } = context.props
    const labelContent =
      intlIt(
        intlObj,
        input.labelToken,
        input.defaultLabel,
        {},
        input.labelParameters,
      ) + context.getMandatoryIndicator(input, context)
    const label = (
      <TextBlock
        content={labelContent}
        isHTML={shouldRenderAsHTML(labelContent, input.defaultLabelHTML)}
      />
    )

    return (
      <AInputHolder
        wrapperStyle={`${input.wrapperStyle}, input-holder-selector-wrapper`}
      >
        <AInput
          id={input.fieldName}
          cmpRef={input.fieldName}
          label={label}
          onChange={(e) => {
            this.onInputChange(input, e.target.value)
          }}
          type={FormConstants.CHECKBOX}
          checked={context.isInputSelected(input, context)}
          style={context.getInputStyle(input, context)}
          help={context.getInputError(input, context)}
        />
        {context.renderNotes(input, context)}
      </AInputHolder>
    )
  }

  renderLabelInput(input, context) {
    const { intlObj } = context.props
    let content = input.defaultContent
    let style = ''
    if (input.style !== undefined && !input.html) {
      style = input.style
    }
    if (!input.html) {
      content =
        intlIt(
          intlObj,
          input.contentToken,
          input.defaultContent,
          {},
          input.contentParameters,
        ) + context.getMandatoryIndicator(input, context)
    }

    return (
      <AInputHolder
        wrapperStyle={`${input.wrapperStyle}, input-holder-label-wrapper`}
      >
        <AInputHolder type={AInputHolderConstants.TYPE_LABEL}>
          <TextBlock
            className={style}
            content={content}
            isHTML={shouldRenderAsHTML(content, input.html)}
          />
          {context.renderNotes(input, context)}
        </AInputHolder>
      </AInputHolder>
    )
  }

  renderInfoblockInput(input, context) {
    const { intlObj } = context.props
    let content = input.defaultContent
    if (!input.contentHTML) {
      content = intlIt(
        intlObj,
        input.contentToken,
        input.defaultContent,
        {},
        input.contentParameters,
      )
    }
    const contentBlock = (
      <TextBlock content={content} isHTML={input.contentHTML} />
    )

    let header = input.defaultHeader
    let headerBlock
    if (input.headerHTML) {
      headerBlock = <TextBlock content={header} isHTML={input.headerHTML} />
      header = null
    } else {
      header = intlIt(
        intlObj,
        input.headerToken,
        input.defaultHeader,
        {},
        input.headerParameters,
      )
    }

    let footer = input.defaultFooter
    let footerBlock
    if (!input.footerHTML) {
      footer = intlIt(
        intlObj,
        input.footerToken,
        input.defaultFooter,
        {},
        input.footerParameters,
      )
    }

    if (input.defaultFooter !== null || input.footerToken !== null) {
      footerBlock = <TextBlock content={footer} isHTML={input.footerHTML} />
    }

    return (
      <AInputHolder wrapperStyle={input.wrapperStyle}>
        <AExpandPanelCustom
          defaultOpen={input.open}
          headerOpenLabel={header}
          headerOpen={headerBlock}
          footer={footerBlock}
        >
          {contentBlock}
        </AExpandPanelCustom>
      </AInputHolder>
    )
  }

  renderTemplateCurrentValues(templates, context) {
    let templatesUI = null
    if (templates !== undefined && templates.length > 0) {
      templatesUI = templates.length.map((template, i) => (
        <div key={i}>{this.renderChildren(template.children, context)}</div>
      ))
    }

    return <AInputHolder>{templatesUI}</AInputHolder>
  }

  renderMultiChildInput(input, context) {
    const { intlObj } = context.props
    let templatesUI = null
    let actionUI = null
    let numCurrentTemplateValues = 0
    if (
      input.currentTemplateValues !== undefined &&
      input.currentTemplateValues.length > 0
    ) {
      numCurrentTemplateValues = input.currentTemplateValues.length
    }

    if (numCurrentTemplateValues < input.maxInstances) {
      actionUI = (
        <ABaseLink
          onClick={() => {
            context.onMultichildInputClick(input, context)
          }}
        >
          {intlIt(
            intlObj,
            input.addChildLabelToken,
            input.addChildDefaultLabel,
          )}
        </ABaseLink>
      )
    }

    if (numCurrentTemplateValues > 0) {
      templatesUI = input.currentTemplateValues.map((template, i) => {
        let childrenUI = null
        if (input.twoColumnDisplay) {
          childrenUI = context.renderChildrenTwoCol(template.template, context)
        } else {
          childrenUI = this.renderChildren(template.template, context)
        }

        let deleteUI = null
        if (
          numCurrentTemplateValues > input.minInstances &&
          template.removeAllowed
        ) {
          deleteUI = (
            <Cont align={ALIGN_RIGHT}>
              <ABaseLink
                onClick={() => {
                  context.onMultichildDeleteClick(input, template, context)
                }}
              >
                {intlIt(
                  intlObj,
                  input.deleteChildLabelToken,
                  input.deleteChildDefaultLabel,
                )}
              </ABaseLink>
            </Cont>
          )
        }

        if (!input.grouped) {
          return (
            <div key={i}>
              {childrenUI}
              {deleteUI}
              <Section style={SUB}></Section>
            </div>
          )
        }

        return (
          <div key={i}>
            <HighlightContainer style={HIGHLIGHT_NOTIFICATION} boxShadow={true}>
              {childrenUI}
              {deleteUI}
            </HighlightContainer>
            <Section style={SUB}></Section>
          </div>
        )
      })
    }

    return (
      <AInputHolder>
        {templatesUI}
        {actionUI}
      </AInputHolder>
    )
  }

  renderChildrenTwoCol(children, context) {
    let childrenUI = null
    if (children !== undefined && children.length > 0) {
      const doubleChildren = []
      for (let i = 0; i < children.length; i++) {
        if (!isNumberEven(i)) {
          doubleChildren.push([children[i - 1], children[i]])
        } else if (i === children.length - 1) {
          doubleChildren.push([children[i], null])
        }
      }
      childrenUI = doubleChildren.map((doubleChild, i) => {
        let inputUI1 = null
        let inputUI2 = null

        if (doubleChild[0] !== null) {
          inputUI1 = context.renderInput(doubleChild[0], context)
        }
        if (doubleChild[1] !== null) {
          inputUI2 = context.renderInput(doubleChild[1], context)
        }
        const ui2 = (
          <ARow key={i}>
            <ACol md={6}>{inputUI1}</ACol>
            <ACol md={6}>{inputUI2}</ACol>
          </ARow>
        )

        return ui2
      })
    }

    return childrenUI
  }

  onMultichildInputClick(input, context) {
    let newLocalData = cloneDeepJSON(context.state.data)
    newLocalData = actOnData(
      newLocalData,
      {
        name: 'add',
        fieldName: input.fieldName,
        template: input.template,
      },
      this.multichildCtrs,
    )

    const newFieldValues = context.composefieldValues(newLocalData)
    const currFieldValues = cloneDeepJSON(context.state.fieldValues)
    const megedFieldValues = context.mergeValuesObj(
      newFieldValues,
      currFieldValues,
    )

    context.setState({
      ...context.state,
      data: newLocalData,
      fieldValues: megedFieldValues,
    })
  }

  onMultichildDeleteClick(input, template, context) {
    let newLocalData = cloneDeepJSON(context.state.data)
    newLocalData = actOnData(
      newLocalData,
      {
        name: 'delete',
        fieldName: input.fieldName,
        template,
      },
      this.multichildCtrs,
    )

    context.setState({
      ...context.state,
      data: newLocalData,
    })
  }

  renderFormInput(input, context) {
    const { intlObj, onSubmitSubFormRequest } = context.props
    const actionLabel = intlIt(
      intlObj,
      input.actionLabelToken,
      input.actionDefaultLabel,
      {},
      {},
    )
    let childrenUI = null
    let errorUI = null

    childrenUI = this.renderChildren(input.children, context)

    if (!isEmpty(input.errorDefaultLabel) || !isEmpty(input.errorLabelToken)) {
      errorUI = (
        <ErrorInline>
          {getInlineError(
            intlIt(
              intlObj,
              input.errorLabelToken,
              input.errorDefaultLabel,
              {},
              {},
            ),
          )}
        </ErrorInline>
      )
    }

    const formInputUI = (
      <div>
        {errorUI}
        {childrenUI}
        <Cont align={ALIGN_RIGHT}>
          <AButtonStandard
            onClick={() => {
              const fieldValues = context.validateFormInputs(input)
              if (
                context.getErrorCount(fieldValues.fieldErrors) <= 0 &&
                onSubmitSubFormRequest !== undefined
              ) {
                onSubmitSubFormRequest(fieldValues.fieldValuesObj)
              }
            }}
            cmpRef={`${actionLabel}-button`}
          >
            {actionLabel}
          </AButtonStandard>
        </Cont>
      </div>
    )

    let formTopPadding = <div></div>

    if (input.collapsible) {
      formTopPadding = <Section style={SUB}></Section>
    }

    let wrappedFormUI = (
      <div>
        {formTopPadding}
        {formInputUI}
        <Section style={SUB}></Section>
      </div>
    )

    if (input.grouped) {
      wrappedFormUI = (
        <div>
          {formTopPadding}
          <HighlightContainer style={HIGHLIGHT_NOTIFICATION} boxShadow={true}>
            {formInputUI}
          </HighlightContainer>
          <Section style={SUB}></Section>
        </div>
      )
    }

    if (input.collapsible) {
      const collapseLabel = intlIt(
        intlObj,
        input.collapseToken,
        input.defaultCollapseLabel,
        {},
        {},
      )

      return (
        <SimpleExpandPanel
          headerOpenText={collapseLabel}
          content={wrappedFormUI}
          defaultOpen={context.getCollapseState(input, !input.collapsed)}
          onToggle={(state) => {
            context.setCollapseState(input, state)
          }}
        />
      )
    }

    return wrappedFormUI
  }

  renderDateInput(input, context) {
    const { intlObj } = context.props
    const label =
      intlIt(
        intlObj,
        input.labelToken,
        input.defaultLabel,
        {},
        input.labelParameters,
      ) + context.getMandatoryIndicator(input, context)
    let placeholder = intlIt(intlObj, 'external.labels.enter.date', '')
    if (!isEmpty(input.placeholder)) {
      placeholder = input.placeholder
    }

    const currLiteralValue = context.getInputCurrentValue(input, context)
    const currValue = isEmpty(currLiteralValue)
      ? null
      : createDateFromYYYYMMDD(currLiteralValue)

    return (
      <AInputHolder wrapperStyle={input.wrapperStyle}>
        <AInputDate
          label={label}
          cmpRef={input.fieldName}
          placeholder={placeholder}
          style={context.getInputStyle(input, context)}
          help={context.getInputError(input, context)}
          value={currValue}
          selectorType={input.selectorType}
          minDate={getMinDate(input)}
          maxDate={getMaxDate(input)}
          onChange={(value) => {
            let formattedVal = null
            if (value !== null) {
              formattedVal = createYYYYMMDDFromDate(value)
            }
            this.onInputChange(input, formattedVal)
          }}
        />
        {context.renderNotes(input, context)}
      </AInputHolder>
    )
  }

  renderTimeInput(input, context) {
    const { intlObj } = context.props
    const label =
      intlIt(
        intlObj,
        input.labelToken,
        input.defaultLabel,
        {},
        input.labelParameters,
      ) + context.getMandatoryIndicator(input, context)

    return (
      <AInputHolder wrapperStyle={input.wrapperStyle}>
        <AInputTime
          id={input.fieldName}
          label={label}
          cmpRef={input.fieldName}
          style={context.getInputStyle(input, context)}
          help={context.getInputError(input, context)}
          value={context.getInputCurrentValue(input, context)}
          use24HourFormat={input.use24HourFormat}
          onChange={(value) => {
            this.onInputChange(input, value)
          }}
        />
        {context.renderNotes(input, context)}
      </AInputHolder>
    )
  }

  renderCurrencyInput(input, context) {
    const { intlObj } = context.props
    const label =
      intlIt(
        intlObj,
        input.labelToken,
        input.defaultLabel,
        {},
        input.labelParameters,
      ) + context.getMandatoryIndicator(input, context)
    let placeholder = ''
    if (!isEmpty(input.placeholder)) {
      placeholder = input.placeholder
    }

    return (
      <AInputHolder wrapperStyle={input.wrapperStyle}>
        <AInputCurrency
          id={input.fieldName}
          label={label}
          cmpRef={input.fieldName}
          placeholder={placeholder}
          style={context.getInputStyle(input, context)}
          help={context.getInputError(input, context)}
          value={context.getInputCurrentValue(input, context)}
          currencyNameSymbol={input.currencyNameSymbol}
          minNumber={getMinNumber(input)}
          maxNumber={getMaxNumber(input)}
          onChange={(value) => {
            this.onInputChange(input, value)
          }}
        />
        {context.renderNotes(input, context)}
      </AInputHolder>
    )
  }

  renderNote(input, context) {
    const { intlObj } = context.props
    let content = input.defaultContent
    let style = ''
    if (input.style !== undefined && !input.html) {
      style = input.style
    }
    if (!input.html) {
      content =
        intlIt(
          intlObj,
          input.contentToken,
          input.defaultContent,
          {},
          input.contentParameters,
        ) + context.getMandatoryIndicator(input, context)
    }

    return (
      <TextBlock
        className={style}
        content={content}
        isHTML={shouldRenderAsHTML(content, input.html)}
      />
    )
  }

  renderImageInput(input, context) {
    const { intlObj } = context.props
    let cmpLabel = null
    if (!isEmpty(input.labelToken) || !isEmpty(input.defaultLabel)) {
      cmpLabel = intlIt(
        intlObj,
        input.labelToken,
        input.defaultLabel,
        {},
        input.labelParameters,
      )
    }

    return (
      <Section>
        <AImg imageURL={input.imageURL} cmpLabel={cmpLabel} />
      </Section>
    )
  }

  renderNotes(input, context) {
    if (input.notes !== undefined && input.notes.length > 0) {
      const notesUI = input.notes.map((note, i) => (
        <div className="" key={i}>
          {context.renderNote(note, context)}
        </div>
      ))

      return (
        <AInputHolder type={AInputHolderConstants.TYPE_NOTES} formGroup>
          {notesUI}
        </AInputHolder>
      )
    }

    return null
  }

  getMandatoryIndicator(input, context) {
    const { intlObj } = context.props
    let mandatorylabel = ''
    const mandatory = isMandatory(input)
    if (mandatory) {
      mandatorylabel = getMandatoryIndicator(intlObj)
    }

    return mandatorylabel
  }

  isInputSelected(input, context) {
    let selected = false
    const currValue = context.getInputCurrentValue(input, context)
    // Only treat as selected when value is explicitly the string 'true'
    // (typeSelector with currentValue: null must show unchecked)
    if (currValue === 'true') {
      selected = true
    }

    return selected
  }

  renderInput(input, context) {
    let inputUI = null
    if (input.type === TYPE_TEXT) {
      inputUI = context.renderTextInput(input, context)
    } else if (input.type === TYPE_OPTIONS) {
      inputUI = context.renderOptionsInput(input, context)
    } else if (input.type === TYPE_SELECTOR) {
      inputUI = context.renderSelectorInput(input, context)
    } else if (input.type === TYPE_LABEL) {
      inputUI = context.renderLabelInput(input, context)
    } else if (input.type === TYPE_IMAGE) {
      inputUI = context.renderImageInput(input, context)
    } else if (input.type === TYPE_INFOBLOCK) {
      inputUI = context.renderInfoblockInput(input, context)
    } else if (input.type === TYPE_MULTICHILD) {
      inputUI = context.renderMultiChildInput(input, context)
    } else if (input.type === TYPE_FORM) {
      inputUI = context.renderFormInput(input, context)
    } else if (input.type === TYPE_DATE) {
      inputUI = context.renderDateInput(input, context)
    } else if (input.type === TYPE_TIME) {
      inputUI = context.renderTimeInput(input, context)
    } else if (input.type === TYPE_CURRENCY) {
      inputUI = context.renderCurrencyInput(input, context)
    }
    let transitionAppear = false
    if (
      context.lastChangedInput !== null &&
      context.lastChangedInput.fieldName === input.fieldName
    ) {
      transitionAppear = true
    }
    const childrenUI = context.renderDependentChildren(
      input,
      context,
      transitionAppear,
    )

    return (
      <div>
        {inputUI}
        {childrenUI}
      </div>
    )
  }

  renderDependentChildren(input, context, transitionAppear = false) {
    let retUI = null
    let ui = null
    if (context.state.fieldValues[input.fieldName] !== undefined) {
      if (input.options !== undefined && input.options.length > 0) {
        for (const option of input.options) {
          if (context.state.fieldValues[input.fieldName] !== undefined) {
            if (context.state.fieldValues[input.fieldName] === option.value) {
              // console.log('renderDependentChildren: current input/last input/animate: ' + input.fieldName + '/' + context.lastChangedInput.fieldName + '/' + animate)
              ui = context.renderChildren(option.children, context)
            }
          }
        }
      }
    }
    if (ui !== null) {
      retUI = (
        <SimpleAnimate
          componentType={CMP_TYPE_DIV}
          transitionAppear={transitionAppear}
        >
          {ui}
        </SimpleAnimate>
      )
    }

    return retUI
  }

  renderChildren(children, context, layout = null) {
    const childrenUI = children.map((input, i) => {
      let ui = <div key={i}>{context.renderInput(input, context)}</div>

      if (layout !== null) {
        if (input.layout !== layout) {
          ui = null
        }
      }

      return ui
    })

    return childrenUI
  }

  renderSection(section, context) {
    const { intlObj } = context.props
    const label = intlIt(
      intlObj,
      section.labelToken,
      section.defaultLabel,
      {},
      section.labelParameters,
    )
    let labelUI
    if (!isEmpty(label) && !isEmpty(label.trim())) {
      labelUI = <div className="cart-main-title">{label}</div>
    }
    let sectionUI = null
    if (!isEmpty(section.children) && section.children.length > 0) {
      if (section.withLayout) {
        const sectionTop = context.renderChildren(
          section.children,
          context,
          LAYOUT_TOP,
        )
        const sectionBottom = context.renderChildren(
          section.children,
          context,
          LAYOUT_BOTTOM,
        )
        const sectionLeft = context.renderChildren(
          section.children,
          context,
          LAYOUT_LEFT,
        )
        const sectionRight = context.renderChildren(
          section.children,
          context,
          LAYOUT_RIGHT,
        )
        sectionUI = (
          <div>
            <Section>{sectionTop}</Section>
            <ARow>
              <ACol sm={4}>{sectionLeft}</ACol>
              <ACol sm={8}>{sectionRight}</ACol>
            </ARow>
            {sectionBottom}
          </div>
        )
      } else {
        sectionUI = context.renderChildren(section.children, context)
      }
    }
    if (!section.standalone) {
      return <div>{sectionUI}</div>
    }

    if (context.props.hideSections) {
      return <Cont>{sectionUI}</Cont>
    }

    return (
      <BoxContainer header={labelUI} encapsulateContent={false}>
        {sectionUI}
      </BoxContainer>
    )
  }

  getForm() {
    const { data } = this.state
    const context = this
    let form = null
    if (!isEmpty(data.sections) && data.sections.length > 0) {
      form = data.sections.map((section, i) => (
        <Section key={i}>{context.renderSection(section, context)}</Section>
      ))
    }

    return form
  }

  // **************Utility Functions**************
  // *****************************************
  getDefaultValuesFromInputs(inputs) {
    const fieldValues = {}
    for (const input of inputs) {
      if (!input || !input.fieldName) {
        continue
      }
      let currentValue = null
      if (
        !input.multiSelect &&
        input.currentValueLite !== undefined &&
        !isEmpty(input.currentValueLite)
      ) {
        currentValue = input.currentValueLite.value
      } else if (
        !input.multiSelect &&
        input.currentValue !== undefined &&
        !isEmpty(input.currentValue)
      ) {
        currentValue = input.currentValue.value
      } else if (
        input.multiSelect &&
        input.currentValueListLite !== undefined &&
        !isEmpty(input.currentValueListLite)
      ) {
        currentValue = input.currentValueListLite.map((item) => item.value)
      } else if (
        input.multiSelect &&
        input.currentValueList !== undefined &&
        !isEmpty(input.currentValueList)
      ) {
        currentValue = input.currentValueList.map((item) => item.value)
      }
      if (currentValue !== null) {
        fieldValues[input.fieldName] = currentValue
      }
    }

    return fieldValues
  }

  composefieldValuesFromInputs(inputs) {
    const fieldValues = {}
    for (const input of inputs) {

      if (!input.fieldName) {
        continue
      }
      let currentValue = null

      // typeSelector strict mode (e.g. pass checkboxes): preserve explicit boolean selector
      // states from API and only fallback to empty when currentValue is missing/unrecognized.
      if (
        this.props.useStrictTypeSelectorCurrentValues &&
        input.type === TYPE_SELECTOR
      ) {
        const selectorValue =
          input.currentValue !== null && !isEmpty(input.currentValue)
            ? input.currentValue.value
            : null
        if (selectorValue === 'true' || selectorValue === 'false') {
          currentValue = selectorValue
        } else {
          currentValue = ''
        }
        fieldValues[input.fieldName] = currentValue
        continue
      }

      if (
        !input.multiSelect &&
        input.currentValueLite !== undefined &&
        !isEmpty(input.currentValueLite)
      ) {
        currentValue = input.currentValueLite.value
      } else if (
        !input.multiSelect &&
        input.currentValue !== undefined &&
        !isEmpty(input.currentValue)
      ) {
        currentValue = input.currentValue.value
      } else if (
        input.multiSelect &&
        input.currentValueListLite !== undefined &&
        !isEmpty(input.currentValueListLite)
      ) {
        currentValue = []
        for (const item of input.currentValueListLite) {
          currentValue.push(item.value)
        }
      } else if (
        input.multiSelect &&
        input.currentValueList !== undefined &&
        !isEmpty(input.currentValueList)
      ) {
        currentValue = []
        for (const item of input.currentValueList) {
          currentValue.push(item.value)
        }
      }
      // If no value found in data structure, check state.fieldValues as fallback for
      // newly added items. Only when useStrictCurrentValues is true to avoid
      // affecting other flows (e.g. pre-checkin).
      const useStrictValues = this.props.useStrictCurrentValues
      if (
        useStrictValues &&
        currentValue === null &&
        this.state &&
        this.state.fieldValues &&
        this.state.fieldValues[input.fieldName] !== undefined
      ) {
        currentValue = this.state.fieldValues[input.fieldName]
      }
      if (currentValue !== null) {
        fieldValues[input.fieldName] = currentValue
      }
    }

    return fieldValues
  }

  composefieldValues(data) {
    const inputs = this.iterateData(data, false)
    let fieldValues = this.composefieldValuesFromInputs(inputs)
    if (!isEmpty(this.props.currFieldValues)) {
      fieldValues = this.mergeIntoQuery(fieldValues, this.props.currFieldValues)
    }

    return fieldValues
  }

  mergeIntoQuery(currQuery, newQuery) {
    for (const key in newQuery) {
      if (!has(currQuery, key) && has(newQuery, key)) {
        currQuery[key] = newQuery[key]
      } else if (has(newQuery, key)) {
        currQuery[key] = newQuery[key]
      }
    }

    return currQuery
  }

  isOptionSelected(input, option, context) {
    let selected = false
    if (context.state.fieldValues[input.fieldName] !== undefined) {
      if (context.state.fieldValues[input.fieldName] === option.value) {
        selected = true
      }
    }

    return selected
  }

  getInputCurrentValue(input, context, nullify = false) {
    let currValue = ''
    if (nullify) {
      currValue = null
    }
    if (context.state.fieldValues[input.fieldName] !== undefined) {
      currValue = context.state.fieldValues[input.fieldName]
    }

    return currValue
  }

  getInputError(input, context, nullify = false) {
    let error
    if (nullify) {
      error = null
    }
    if (context.state.fieldErrors[input.fieldName] !== undefined) {
      error = context.state.fieldErrors[input.fieldName]
    }

    return error
  }

  getInputStyle(input, context) {
    let style
    if (context.getInputError(input, context, true) !== null) {
      style = ERROR_CLASS
    }

    return style
  }

  generateValueAndErrorForInputs(inputs = [], allFields = {}, allErrors = {}) {
    const { intlObj } = this.props

    for (const input of inputs) {
      let value = null
      if (this.state.fieldValues[input.fieldName] !== undefined) {
        value = this.state.fieldValues[input.fieldName]
      }
      allFields[input.fieldName] = value
      const errors = validateInput(input, value, intlObj)
      let error = null
      if (!isEmpty(errors) && errors.length > 0) {
        error = errors[0]
      }
      allErrors[input.fieldName] = error
    }
  }

  validateAllInputs(validationRequestId) {
    const { data } = this.state
    const inputs = this.iterateData(data, true)
    const allErrors = {}
    const allFields = {}

    this.generateValueAndErrorForInputs(inputs, allFields, allErrors)

    addMultichildCtrsToObject(allFields, this.multichildCtrs)
    this.setState({
      ...this.state,
      fieldErrors: allErrors,
      validationRequestId,
    })
    this.props.onValidation(
      validationRequestId,
      this.getErrorCount(allErrors),
      convertObjToDynamicDataResponse(allFields),
    )
  }

  validateFormInputs(form) {
    const inputs = this.iterateChildrenData(form, true)
    const formErrors = {}
    const formFields = {}

    this.generateValueAndErrorForInputs(inputs, formFields, formErrors)

    const allErrors = {}
    const allFields = {}
    const { data } = this.state
    const allInputs = this.iterateData(data, true)

    this.generateValueAndErrorForInputs(allInputs, allFields, allErrors)

    allFields.subFormSubmit = form.fieldName

    this.setState({
      ...this.state,
      fieldErrors: formErrors,
    })

    return {
      fieldErrors: formErrors,
      fieldValuesObj: convertObjToDynamicDataResponse(
        merge(formFields, allFields),
      ),
    }
  }

  getErrorCount(fieldErrors) {
    let count = 0
    for (const key in fieldErrors) {
      if (fieldErrors[key] !== null) {
        count += 1
      }
    }

    return count
  }

  setCollapseState(input, state) {
    this.collapseStates[input.fieldName] = state
  }

  getCollapseState(input, dataState) {
    if (this.collapseStates[input.fieldName] !== undefined) {
      return this.collapseStates[input.fieldName]
    }

    return dataState
  }

  iterateData(data, currentOnly) {
    const inputs = []
    if (!isEmpty(data.sections) && data.sections.length > 0) {
      for (const section of data.sections) {
        this.iterateSection(section, inputs, currentOnly)
      }
    }

    return inputs
  }

  iterateChildrenData(data, currentOnly) {
    const inputs = []
    if (!isEmpty(data.children) && data.children.length > 0) {
      this.iterateChildren(data.children, inputs, currentOnly)
    }

    return inputs
  }

  iterateSection(section, inputs, currentOnly) {
    if (!isEmpty(section.children) && section.children.length > 0) {
      this.iterateChildren(section.children, inputs, currentOnly)
    }
  }

  iterateChildren(children, inputs, currentOnly) {
    if (!isEmpty(children) && children.length > 0) {
      for (const input of children) {
        this.iterateInput(input, inputs, currentOnly)
      }
    }
  }

  iterateInputOptions(input, inputs, currentOnly) {
    let currentValue = this.getInputCurrentValue(input, this, true)
    // When building fieldValues, state may be empty so getInputCurrentValue returns null.
    // Fall back to input.currentValue/currentValueLite only when useStrictCurrentValues is set.
    if (this.props.useStrictCurrentValues && (currentValue === null || currentValue === '')) {
      if (input.currentValueLite !== undefined && !isEmpty(input.currentValueLite)) {
        currentValue = input.currentValueLite.value
      } else if (input.currentValue !== undefined && !isEmpty(input.currentValue)) {
        currentValue = input.currentValue.value
      }
    }
    if (input.options !== undefined && input.options.length > 0) {
      for (const option of input.options) {
        if (currentOnly) {
          // console.log('currentValue/option.value ' + currentValue + '/' + option.value)
          if (currentValue === option.value) {
            this.iterateChildren(option.children, inputs, currentOnly)
          }
        } else {
          this.iterateChildren(option.children, inputs, currentOnly)
        }
      }
    }
  }

  iterateInputMultichildValues(input, inputs, currentOnly) {
    if (
      input.currentTemplateValues !== undefined &&
      input.currentTemplateValues.length > 0
    ) {
      // When useStrictCurrentValues is true, always use currentOnly=true when
      // processing currentTemplateValues so we only collect selected values,
      // not template defaults from unselected options. Otherwise use currentOnly
      // to avoid affecting pre-checkin and other flows.
      const effectiveCurrentOnly = this.props.useStrictCurrentValues
        ? true
        : currentOnly
      for (const templateValue of input.currentTemplateValues) {
        this.iterateChildren(templateValue.template, inputs, effectiveCurrentOnly)
      }
    }
  }

  iterateInput(input, inputs, currentOnly) {
    if (input !== undefined) {
      inputs.push(input)

      // IMPORTANT: For multichild inputs with currentTemplateValues, ONLY process currentTemplateValues
      // and skip options entirely. currentTemplateValues contains the actual populated data,
      // while options contains template structures that may have incorrect default values.
      // Processing both would cause template structure values to overwrite correct populated values.
      if (
        input.currentTemplateValues !== undefined &&
        input.currentTemplateValues.length > 0
      ) {
        this.iterateInputMultichildValues(input, inputs, currentOnly)
      } else if (input.options !== undefined && input.options.length > 0) {
        this.iterateInputOptions(input, inputs, currentOnly)
      }
    }
  }

  mergeValuesObj(newValueObj, currValueObj) {
    for (const key in newValueObj) {
      if (!has(currValueObj, key) && has(newValueObj, key)) {
        currValueObj[key] = newValueObj[key]
      }
    }

    return currValueObj
  }

  isSelectDisabled(optionsList, currentValue) {
    for (const item of optionsList) {
      if (!item.props.disabled && currentValue !== item.props.value) {
        return false
      }
    }

    return true
  }

  pStart() {
    this.startTime = new Date()
  }

  pEnd() {
    this.endTime = new Date()
  }

  static defaultProps = {
    currFieldValues: {},
    hideSections: false,
    useStrictCurrentValues: false,
    useStrictTypeSelectorCurrentValues: false,
  }

  static propTypes = {
    data: PropTypes.object.isRequired,
    intlObj: PropTypes.object.isRequired,
    validationRequestId: PropTypes.number.isRequired,
    onValidation: PropTypes.func.isRequired,
    onSubmitSubFormRequest: PropTypes.func,
    currFieldValues: PropTypes.object,
    hideSections: PropTypes.bool,
    /** When true, uses strict current-values-only behavior (multichild currentOnly, option fallback, state fallback). Set for flows that need it (e.g. booking-details with multichild); leave false for pre-checkin etc. */
    useStrictCurrentValues: PropTypes.bool,
    /** When true, applies strict currentValue handling for TYPE_SELECTOR inputs only. */
    useStrictTypeSelectorCurrentValues: PropTypes.bool,
  }
}

export default DynamicDataCmp
